import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card, Row, Col, Statistic, Table, Button, Tag, Empty, message,
  Modal, Form, Input, Tabs, Alert, Divider, Avatar, Descriptions, Progress, Space
} from 'antd';
import {
  UserOutlined, DollarOutlined, CalendarOutlined, LockOutlined,
  EditOutlined, DownloadOutlined, BellOutlined, SmileOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';

const fmt = (v: any) => '₦' + parseFloat(String(v || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const EmployeeDashboard: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [payslips, setPayslips] = useState<any[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [pwdModal, setPwdModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [pwdForm] = Form.useForm();
  const [profileForm] = Form.useForm();

  const employeeId = user?.id || user?.employee_id;
  const tenantId = apiService.getTenantId();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [pr, psr, lbr] = await Promise.all([
        fetch(`/backend/hr-api/employees/self_service.php?action=profile&employee_id=${employeeId}&tenant_id=${tenantId}`).then(r => r.json()),
        fetch(`/backend/hr-api/employees/self_service.php?action=payslips&employee_id=${employeeId}&tenant_id=${tenantId}`).then(r => r.json()),
        fetch(`/backend/hr-api/employees/self_service.php?action=leave_balance&employee_id=${employeeId}&tenant_id=${tenantId}`).then(r => r.json()),
      ]);
      if (pr.success) { setProfile(pr.data); }
      if (psr.success) setPayslips(psr.data || []);
      if (lbr.success) setLeaveBalance(lbr.data || []);

      // Fetch notifications and tickets via apiService
      const [nr, tr] = await Promise.all([
        apiService.getNotifications?.() || Promise.resolve({ success: false }),
        apiService.getHelpdeskTickets?.() || Promise.resolve({ success: false }),
      ]);
      if (nr.success) setNotifications(nr.data || []);
      if (tr.success) setTickets((tr.data || []).filter((t: any) => t.employee_id === employeeId));
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async (values: any) => {
    const r = await fetch(`/backend/hr-api/employees/self_service.php?action=change_password&employee_id=${employeeId}&tenant_id=${tenantId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values, employee_id: employeeId, tenant_id: tenantId }),
    }).then(r => r.json());
    if (r.success) { message.success('Password changed successfully'); setPwdModal(false); pwdForm.resetFields(); }
    else message.error(r.error || 'Failed to change password');
  };

  const handleProfileUpdate = async (values: any) => {
    const r = await fetch(`/backend/hr-api/employees/self_service.php?action=update_profile&employee_id=${employeeId}&tenant_id=${tenantId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values, employee_id: employeeId, tenant_id: tenantId }),
    }).then(r => r.json());
    if (r.success) { message.success('Profile updated'); setProfileModal(false); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const latestPayslip = payslips[0];

  const overviewTab = (
    <div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}><Card><Statistic title="Net Pay (Latest)" value={latestPayslip?.net_pay || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Leave Balance" value={leaveBalance.reduce((s: number, l: any) => s + parseFloat(l.remaining || 0), 0)} suffix="days" precision={1} /></Card></Col>
        <Col span={6}><Card><Statistic title="Open Tickets" value={tickets.filter((t: any) => t.status === 'open').length} prefix={<BellOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="Payslips Available" value={payslips.length} prefix={<DollarOutlined />} /></Card></Col>
      </Row>

      {/* Profile Card */}
      <Card title="My Profile" extra={
        <Button icon={<EditOutlined />} onClick={() => { profileForm.setFieldsValue({ phone: profile?.phone, bank_name: profile?.bank_name, account_number: profile?.account_number, account_name: profile?.account_name }); setProfileModal(true); }}>Edit</Button>
      }>
        {profile && (
          <Row gutter={24}>
            <Col span={4} style={{ textAlign: 'center' }}>
              <Avatar size={80} style={{ background: '#1890ff', fontSize: 32 }}>{profile.first_name?.charAt(0)}</Avatar>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{profile.first_name} {profile.last_name}</div>
              <Tag color="blue">{profile.position}</Tag>
            </Col>
            <Col span={20}>
              <Descriptions column={3} size="small">
                <Descriptions.Item label="Employee ID">{profile.emp_code}</Descriptions.Item>
                <Descriptions.Item label="Department">{profile.department}</Descriptions.Item>
                <Descriptions.Item label="Email">{profile.email}</Descriptions.Item>
                <Descriptions.Item label="Phone">{profile.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="Hire Date">{profile.hire_date}</Descriptions.Item>
                <Descriptions.Item label="Status"><Tag color="success">{profile.status}</Tag></Descriptions.Item>
                <Descriptions.Item label="Bank">{profile.bank_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Account">{profile.account_number || '-'}</Descriptions.Item>
                <Descriptions.Item label="Account Name">{profile.account_name || '-'}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}
      </Card>
    </div>
  );

  const payslipsTab = (
    <Card title="My Payslips" extra={<Tag color="blue">Last 12 months</Tag>}>
      {payslips.length === 0 ? <Empty description="No payslips available yet" /> : (
        <Table dataSource={payslips} rowKey="id" pagination={false} size="small"
          columns={[
            { title: 'Period', dataIndex: 'pay_period', render: (v: string) => <strong>{v}</strong> },
            { title: 'Gross Pay', dataIndex: 'gross_pay', render: fmt },
            { title: 'Deductions', dataIndex: 'total_deductions', render: (v: any) => <span style={{ color: '#ff4d4f' }}>{fmt(v)}</span> },
            { title: 'Net Pay', dataIndex: 'net_pay', render: (v: any) => <strong style={{ color: '#52c41a' }}>{fmt(v)}</strong> },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v==='paid'?'success':v==='approved'?'processing':'warning'}>{v?.toUpperCase()}</Tag> },
            { title: 'Payment Date', dataIndex: 'payment_date' },
            { title: '', render: (_: any, r: any) => (
              <Button size="small" icon={<DownloadOutlined />}
                onClick={async () => { message.loading('Generating...', 1); await apiService.downloadPayslip(r.id); }}>
                PDF
              </Button>
            )},
          ]} />
      )}
    </Card>
  );

  const leaveTab = (
    <Card title="My Leave Balances">
      <Row gutter={16}>
        {leaveBalance.length === 0 ? <Col span={24}><Empty description="No leave entitlements configured yet" /></Col> :
          leaveBalance.map((lb: any, i: number) => (
            <Col span={8} key={i} style={{ marginBottom: 16 }}>
              <Card size="small" title={<Tag color="blue">{lb.leave_type?.replace(/_/g,' ').toUpperCase()}</Tag>}>
                <Progress percent={Math.round((lb.used_balance / lb.days_per_year) * 100)} size="small"
                  strokeColor={parseFloat(lb.remaining) > 5 ? '#52c41a' : '#ff4d4f'}
                  format={() => `${parseFloat(lb.remaining||0).toFixed(1)} days left`} />
                <Row style={{ marginTop: 8, fontSize: 12 }}>
                  <Col span={12}><span style={{ color: '#999' }}>Entitlement:</span> {lb.days_per_year} days</Col>
                  <Col span={12}><span style={{ color: '#999' }}>Used:</span> {parseFloat(lb.used_balance||0).toFixed(1)} days</Col>
                </Row>
              </Card>
            </Col>
          ))
        }
      </Row>
      <Divider />
      <Button type="primary" onClick={() => window.location.href = '/leave'}>Request Leave</Button>
    </Card>
  );

  const ticketsTab = (
    <Card title="My Support Tickets" extra={<Button type="primary" onClick={() => window.location.href = '/helpdesk'}>New Ticket</Button>}>
      <Table dataSource={tickets} rowKey="id" pagination={false} size="small"
        columns={[
          { title: 'Ticket', dataIndex: 'ticket_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
          { title: 'Subject', dataIndex: 'subject' },
          { title: 'Category', dataIndex: 'category', render: (v: string) => <Tag>{v}</Tag> },
          { title: 'Priority', dataIndex: 'priority', render: (v: string) => <Tag color={v==='high'?'error':v==='medium'?'warning':'default'}>{v}</Tag> },
          { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v==='open'?'processing':v==='resolved'?'success':'default'}>{v}</Tag> },
          { title: 'Created', dataIndex: 'created_at', render: (v: string) => v?.split('T')[0] },
        ]} />
    </Card>
  );

  const securityTab = (
    <Card title="Account Security">
      <Row gutter={24}>
        <Col span={12}>
          <Card size="small" title="Password">
            <p style={{ color: '#666', marginBottom: 16 }}>Keep your account secure by using a strong, unique password.</p>
            <Button type="primary" icon={<LockOutlined />} onClick={() => setPwdModal(true)}>Change Password</Button>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title="Login Activity">
            <p style={{ color: '#666' }}>You are currently logged in as:</p>
            <p><strong>{profile?.first_name} {profile?.last_name}</strong></p>
            <p style={{ fontSize: 12, color: '#999' }}>{profile?.email}</p>
            <Tag color="success">Active Session</Tag>
          </Card>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0 }}>Welcome back, {profile?.first_name || user?.first_name || 'Employee'} 👋</h1>
          <p style={{ color: '#666', margin: 0 }}>{profile?.position} • {profile?.department}</p>
        </div>
        <Button icon={<LockOutlined />} onClick={() => setPwdModal(true)}>Change Password</Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'overview', label: '🏠 Overview', children: overviewTab },
        { key: 'payslips', label: '💰 Payslips', children: payslipsTab },
        { key: 'leave', label: '🌴 Leave', children: leaveTab },
        { key: 'tickets', label: '🎫 Support Tickets', children: ticketsTab },
        { key: 'security', label: '🔒 Security', children: securityTab },
      ]} />

      {/* Password Change Modal */}
      <Modal title="Change Password" open={pwdModal}
        onCancel={() => setPwdModal(false)}
        closable={true} footer={null} width={450}>
        <Form form={pwdForm} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item name="current_password" label="Current Password (leave blank if not set)">
            <Input.Password placeholder="Enter current or temporary password" />
          </Form.Item>
          <Form.Item name="new_password" label="New Password" rules={[{ required: true }, { min: 6, message: 'At least 6 characters' }]}>
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item name="confirm_password" label="Confirm New Password" rules={[
            { required: true },
            ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue('new_password') === value ? Promise.resolve() : Promise.reject('Passwords do not match'); }})
          ]}>
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
          <Space><Button type="primary" htmlType="submit" icon={<LockOutlined />}>Change Password</Button>
            {!profile?.password_reset_required && <Button onClick={() => setPwdModal(false)}>Cancel</Button>}
          </Space>
        </Form>
      </Modal>

      {/* Profile Update Modal */}
      <Modal title="Update Profile" open={profileModal} onCancel={() => setProfileModal(false)} footer={null} width={500}>
        <Form form={profileForm} layout="vertical" onFinish={handleProfileUpdate}>
          <Form.Item name="phone" label="Phone Number"><Input placeholder="+234 xxx xxx xxxx" /></Form.Item>
          <Divider>Bank Account Details</Divider>
          <Form.Item name="bank_name" label="Bank Name"><Input placeholder="e.g. Zenith Bank" /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="account_number" label="Account Number"><Input maxLength={10} /></Form.Item></Col>
            <Col span={12}><Form.Item name="account_name" label="Account Name"><Input /></Form.Item></Col>
          </Row>
          <Space><Button type="primary" htmlType="submit">Save Changes</Button><Button onClick={() => setProfileModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeDashboard;
